import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  CurrencyEuroIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  TagIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  BoltIcon,
  BeakerIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  EllipsisHorizontalIcon,
  BuildingLibraryIcon,
  HeartIcon,
  UserPlusIcon,
  CreditCardIcon,
  HomeIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  TruckIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

import { FaDog } from 'react-icons/fa';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ConfirmationModal from '../components/ConfirmationModal';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend as RechartsLegend, ResponsiveContainer } from 'recharts';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import BudgetDetailModal from '../components/modals/BudgetDetailModal';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const CARD_BG = '#1c2432';

// Create a typed wrapper for FaDog
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

// Font Awesome to Heroicons mapping
const iconMap: { [key: string]: React.ComponentType<any> } = {
  FaUsers: UserGroupIcon,
  FaBolt: BoltIcon,
  FaTint: BeakerIcon,
  FaUsersCog: Cog6ToothIcon,
  FaTools: WrenchScrewdriverIcon,
  FaShieldAlt: ShieldCheckIcon,
  FaEllipsisH: EllipsisHorizontalIcon,
  FaEuroSign: CurrencyEuroIcon,
  FaBuilding: BuildingLibraryIcon,
  FaTrash: TrashIcon,
  FaHeart: HeartIcon,
  FaUserPlus: UserPlusIcon,
  FaUtensils: HomeIcon,
  FaExchangeAlt: ArrowsRightLeftIcon,
  FaDog: DogIcon,
  FaCar: TruckIcon,
  FaBookOpen: BookOpenIcon,
  // Fallback
  default: TagIcon
};

interface Category {
  id: number;
  name: string;
  icon: string;
}

interface AccountCategory {
  category: string;
  income: number;
  expense: number;
  transactionCount: number;
  icon: string;
}

interface BudgetData {
  [categoryName: string]: {
    income: string | number;
    expense: string | number;
    comment?: string;
  };
}

interface BudgetPlan {
  id: number;
  name: string;
  year: number;
  importYear: number;
  budgetData: BudgetData;
  createdAt?: string;
  updatedAt?: string;
}

interface ConfirmModalState {
  isOpen: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onConfirm?: () => void;
}

// Icon-Mapping für FA-Icons zu Heroicons
const getIconForCategory = (iconName: string) => {
  const iconMap: { [key: string]: React.ComponentType<any> } = {
    'FaUsers': UserGroupIcon,
    'FaBolt': BoltIcon,
    'FaTint': BeakerIcon,
    'FaUsersCog': Cog6ToothIcon,
    'FaTools': WrenchScrewdriverIcon,
    'FaShieldAlt': ShieldCheckIcon,
    'FaEllipsisH': EllipsisHorizontalIcon,
    'FaEuroSign': CurrencyEuroIcon,
    'FaBuilding': BuildingLibraryIcon,
    'FaTrash': TrashIcon,
    'FaHeart': HeartIcon,
    'FaUserPlus': UserPlusIcon,
    'FaMoneyCheckAlt': CreditCardIcon,
    'FaUtensils': HomeIcon,
    'FaExchangeAlt': ArrowsRightLeftIcon,
    'FaMoneyBill': BanknotesIcon,
    'FaDog': HeartIcon,
    'FaCar': TruckIcon,
    'FaBookOpen': BookOpenIcon,
  };

  return iconMap[iconName] || TagIcon;
};

const BudgetPlanning: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [budgetPlans, setBudgetPlans] = useState<BudgetPlan[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [accountCategories, setAccountCategories] = useState<AccountCategory[]>([]);
  const [, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [loading, setLoading] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [, setIsFullscreen] = useState<boolean>(false);
  const [selectedBudgetPlan, setSelectedBudgetPlan] = useState<BudgetPlan | null>(null);
  const [markedPlanId, setMarkedPlanId] = useState<number | null>(null);
  const [planFilterYear, setPlanFilterYear] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'categories' | 'history'>('overview');
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  // Category Input Modal State
  const [categoryInputModal, setCategoryInputModal] = useState<{
    isOpen: boolean;
    title: string;
    onConfirm: (categoryName: string) => void;
  }>({
    isOpen: false,
    title: '',
    onConfirm: () => {}
  });

  const [categoryInputValue, setCategoryInputValue] = useState('');

  // Neue Budget-Planung Formular
  const [, setNewPlanForm] = useState({
    name: '',
    year: new Date().getFullYear() + 1,
    importYear: new Date().getFullYear(),
  });

  // Multi-Step Modal für Budget-Planung
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [, setManualBudgetData] = useState<BudgetData>({});
  const [, setShowManualInput] = useState<boolean>(false);
  const [stepData, setStepData] = useState({
    step1: {
      name: '',
      year: new Date().getFullYear() + 1,
      importYear: new Date().getFullYear(),
      useImport: true
    },
    step2: {
      budgetData: {} as BudgetData
    }
  });

  // Kontoverlauf und Kategorie-Charts
  const [accountStatements, setAccountStatements] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categoryStatements, setCategoryStatements] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [showAdvancedChart, setShowAdvancedChart] = useState<boolean>(true);
  const [, setSelectedPieCategory] = useState<string | null>(null);
  const [, setExpandedCategory] = useState<string | null>(null);

  // Optimierte Chart-Daten vom Backend
  const [optimizedChartData, setOptimizedChartData] = useState<any[]>([]);
  const [, setAvailableCategories] = useState<string[]>([]);
  const [, setChartDataLoading] = useState<boolean>(false);

  // Suppress unused variable warnings (state-setters are kept for backwards compatibility)
  void setSelectedYear;
  void setIsFullscreen;
  void setNewPlanForm;
  void setManualBudgetData;
  void setShowManualInput;
  void setSelectedPieCategory;
  void setExpandedCategory;

  // Berechtigungsprüfung - nur Vorstand darf zugreifen
  const isAuthorized = user && (user.vorstand === 1 || user.admin === 1);

  useEffect(() => {
    if (!isAuthorized) return;

    loadBudgetPlans();
    loadCategories();
    loadAccountCategories(); // Alle Jahre initial
    loadAccounts();
  }, [isAuthorized]);

  // Lade Statements für Ist-Werte sobald Accounts verfügbar sind
  useEffect(() => {
    if (!isAuthorized || accounts.length === 0) return;
    loadAccountStatements();
  }, [isAuthorized, accounts]);

  // Aktualisiere accountCategories wenn eine Planung angeklickt wird
  useEffect(() => {
    if (!isAuthorized) return;
    // Mit Plan-Jahr filtern, oder alle Jahre wenn kein Plan ausgewählt
    loadAccountCategories(planFilterYear || undefined);
  }, [planFilterYear]);

  useEffect(() => {
    if (showAdvancedChart && accounts.length > 0) {
      // Lade optimierte Chart-Daten statt aller Statements
      loadOptimizedChartData(planFilterYear || undefined);
    }
  }, [showAdvancedChart, accounts, planFilterYear]);

  const loadBudgetPlans = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/finance/budget-plans');
      setBudgetPlans(response.data);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Budget-Planungen konnten nicht geladen werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/api/finance/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Kategorien konnten nicht geladen werden:', error);
    }
  };

  const loadAccountCategories = async (year?: number) => {
    try {
      const url = year
        ? `/api/finance/account-categories?year=${year}`
        : '/api/finance/account-categories';
      const response = await apiClient.get(url);
      if (response.data.success) {
        setAccountCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Konto-Kategorien konnten nicht geladen werden:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await apiClient.get('/api/finance/accounts');
      setAccounts(response.data);
    } catch (error) {
      console.error('Konten konnten nicht geladen werden:', error);
    }
  };

  const loadAccountStatements = async () => {
    try {
      // Lade alle Statements in einem einzigen API-Aufruf (statt sequentiell pro Konto)
      const response = await apiClient.get('/api/finance/statements');
      const statements = response.data.map((stmt: any) => ({
        ...stmt,
        account_name: stmt.konto_name || accounts.find(a => a.id === stmt.konto_id)?.name || 'Unbekannt',
        account_id: stmt.konto_id
      }));

      // Sortiere nach Buchungstag
      statements.sort((a: any, b: any) => new Date(a.buchungstag).getTime() - new Date(b.buchungstag).getTime());
      setAccountStatements(statements);
    } catch (error) {
      console.error('Kontoauszüge konnten nicht geladen werden:', error);
    }
  };

  // Optimierte Chart-Daten vom Backend laden (vorberechnet)
  const loadOptimizedChartData = async (year?: number) => {
    setChartDataLoading(true);
    try {
      const url = year
        ? `/api/finance/chart-data?year=${year}`
        : '/api/finance/chart-data';
      const response = await apiClient.get(url);

      if (response.data.success) {
        setOptimizedChartData(response.data.chartData || []);
        setAvailableCategories(response.data.kategorien || []);
        console.log('Optimierte Chart-Daten geladen:', {
          dataPoints: response.data.chartData?.length,
          kategorien: response.data.kategorien?.length,
          year: response.data.year
        });
      }
    } catch (error) {
      console.error('Optimierte Chart-Daten konnten nicht geladen werden:', error);
      // Fallback: Lade alte Statements
      loadAccountStatements();
    } finally {
      setChartDataLoading(false);
    }
  };

  const loadCategoryStatements = async (categoryName: string) => {
    try {
      const filtered = accountStatements.filter(stmt =>
        stmt.categories && stmt.categories.some((cat: any) =>
          categories.find(c => c.id === cat.kategorie_id)?.name === categoryName
        ) &&
        // Filtere Umbuchungen aus (nur echte Buchungen wie beim Kontoverlauf)
        stmt.konto_id && !stmt.von_konto_id && !stmt.nach_konto_id
      );
      setCategoryStatements(filtered);
    } catch (error) {
      console.error('Kategorie-Statements konnten nicht gefiltert werden:', error);
    }
  };

  // Schritt-Navigation
  const nextStep = async () => {
    if (currentStep === 1) {
      // Validierung für Schritt 1
      if (!stepData.step1.name.trim()) {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: 'Bitte geben Sie einen Namen für die Budget-Planung ein.',
          variant: 'single'
        });
        return;
      }

      // Lade Budget-Daten für Schritt 2
      if (stepData.step1.useImport) {
        try {
          const response = await apiClient.get(`/api/finance/account-categories?year=${stepData.step1.importYear}`);
          const budgetData: BudgetData = {};

          if (response.data.success) {
            response.data.categories.forEach((cat: AccountCategory) => {
              budgetData[cat.category] = {
                income: cat.income,
                expense: cat.expense
              };
            });
          }

          setStepData(prev => ({
            ...prev,
            step2: { budgetData }
          }));
        } catch (error) {
          console.error('Fehler beim Laden der Import-Daten:', error);
        }
      } else {
        // Initiale leere Struktur für manuelle Eingabe - zeige alle verfügbaren Kategorien
        try {
          const response = await apiClient.get('/api/finance/categories');
          const initialData: BudgetData = {};

          response.data.forEach((cat: Category) => {
            initialData[cat.name] = {
              income: '',
              expense: ''
            };
          });

          setStepData(prev => ({
            ...prev,
            step2: { budgetData: initialData }
          }));
        } catch (error) {
          console.error('Fehler beim Laden der Kategorien:', error);
        }
      }

      // Zusätzlich: Alle verfügbaren Kategorien laden und mit bestehenden Budget-Daten mergen
      try {
        const allCategoriesResponse = await apiClient.get('/api/finance/categories');
        const allCategories = allCategoriesResponse.data;

        // Merge mit bestehenden Budget-Daten
        setStepData(prev => {
          const currentBudgetData = { ...prev.step2.budgetData };

          // Füge alle verfügbaren Kategorien hinzu, die noch nicht vorhanden sind
          allCategories.forEach((cat: Category) => {
            if (!currentBudgetData[cat.name]) {
              currentBudgetData[cat.name] = {
                income: '',
                expense: ''
              };
            }
          });

          return {
            ...prev,
            step2: { budgetData: currentBudgetData }
          };
        });
      } catch (error) {
        console.error('Fehler beim Laden aller Kategorien:', error);
      }

      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetModal = () => {
    setCurrentStep(1);
    setStepData({
      step1: {
        name: '',
        year: new Date().getFullYear() + 1,
        importYear: new Date().getFullYear(),
        useImport: true
      },
      step2: {
        budgetData: {} as BudgetData
      }
    });
    setShowCreateModal(false);
  };

  // Manuelle Budget-Daten für Schritt 2 ändern
  const handleStep2BudgetChange = (categoryName: string, field: 'income' | 'expense' | 'comment', value: string) => {
    setStepData(prev => ({
      ...prev,
      step2: {
        budgetData: {
          ...prev.step2.budgetData,
          [categoryName]: {
            ...prev.step2.budgetData[categoryName],
            [field]: value
          }
        }
      }
    }));
  };

  // Neue Kategorie in Schritt 2 hinzufügen
  const addStep2Category = () => {
    setCategoryInputModal({
      isOpen: true,
      title: 'Neue Kategorie hinzufügen',
      onConfirm: (categoryName: string) => {
        if (categoryName && categoryName.trim()) {
          setStepData(prev => ({
            ...prev,
            step2: {
              budgetData: {
                ...prev.step2.budgetData,
                [categoryName.trim()]: {
                  income: '',
                  expense: ''
                }
              }
            }
          }));
        }
        setCategoryInputModal({ isOpen: false, title: '', onConfirm: () => {} });
        setCategoryInputValue('');
      }
    });
  };

  // Kategorie aus Schritt 2 entfernen
  const removeStep2Category = (categoryName: string) => {
    setStepData(prev => {
      const newBudgetData = { ...prev.step2.budgetData };
      delete newBudgetData[categoryName];
      return {
        ...prev,
        step2: {
          budgetData: newBudgetData
        }
      };
    });
  };

  const handleCreateBudgetPlan = async () => {
    setLoading(true);
    try {
      const payload = {
        name: stepData.step1.name,
        year: stepData.step1.year,
        importYear: stepData.step1.importYear,
        budgetData: stepData.step2.budgetData
      };

      await apiClient.post('/api/finance/budget-plans', payload);

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Budget-Planung wurde erfolgreich erstellt.',
        variant: 'single'
      });

      resetModal();
      loadBudgetPlans();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Budget-Planung konnte nicht erstellt werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBudgetPlan = (plan: BudgetPlan) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Löschen bestätigen',
      message: `Möchten Sie die Budget-Planung "${plan.name}" wirklich löschen?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/api/finance/budget-plans/${plan.id}`);
          setConfirmModal({
            isOpen: true,
            type: 'success',
            title: 'Erfolgreich',
            message: 'Budget-Planung wurde gelöscht.',
            variant: 'single'
          });
          loadBudgetPlans();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Budget-Planung konnte nicht gelöscht werden.',
            variant: 'single'
          });
        }
      }
    });
  };

  // Statistiken berechnen - reagiert auf markierten Plan
  const statistics = useMemo(() => {
    const totalPlans = budgetPlans.length;
    let totalIncome = 0;
    let totalExpense = 0;
    let istIncome = 0;
    let istExpense = 0;
    let currentYearPlans = 0;

    // Wenn ein Plan markiert ist, nur diesen Plan anzeigen
    const plansToShow = markedPlanId
      ? budgetPlans.filter(p => p.id === markedPlanId)
      : budgetPlans;

    plansToShow.forEach(plan => {
      if (plan.year === new Date().getFullYear() + 1) {
        currentYearPlans++;
      }

      Object.values(plan.budgetData).forEach(data => {
        const income = typeof data.income === 'string' ? parseFloat(data.income) || 0 : data.income || 0;
        const expense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : data.expense || 0;
        totalIncome += income;
        totalExpense += expense;
      });

      // Ist-Werte aus accountStatements berechnen
      const yearFiltered = accountStatements.filter(stmt => {
        const bookingYear = new Date(stmt.buchungstag).getFullYear();
        return bookingYear === plan.year && stmt.konto_id && !stmt.von_konto_id && !stmt.nach_konto_id;
      });

      yearFiltered.forEach(stmt => {
        if (stmt.categories && stmt.betrag > 0) {
          stmt.categories.forEach((cat: any) => { istIncome += cat.betrag; });
        }
        if (stmt.categories && stmt.betrag < 0) {
          stmt.categories.forEach((cat: any) => { istExpense += cat.betrag; });
        }
      });
    });

    const netBudget = totalIncome - totalExpense;
    const istNet = istIncome - istExpense;
    const selectedPlan = markedPlanId ? budgetPlans.find(p => p.id === markedPlanId) : null;

    return { totalPlans, totalIncome, totalExpense, netBudget, currentYearPlans, istIncome, istExpense, istNet, selectedPlan };
  }, [budgetPlans, markedPlanId, accountStatements]);

  // Chart-Daten für zeitlichen Verlauf - Budget-Planungen
  const budgetChartData = useMemo(() => {
    if (!budgetPlans || budgetPlans.length === 0) {
      return [];
    }

    // Group plans by year and aggregate data
    const yearData = new Map<number, { income: number; expense: number }>();

    budgetPlans.forEach(plan => {
      if (!yearData.has(plan.year)) {
        yearData.set(plan.year, { income: 0, expense: 0 });
      }

      const yearTotals = yearData.get(plan.year)!;

      // Calculate totals for this plan
      if (plan.budgetData && typeof plan.budgetData === 'object') {
        Object.values(plan.budgetData).forEach(data => {
          if (data && typeof data === 'object') {
            const income = typeof data.income === 'string' ? parseFloat(data.income) || 0 : (typeof data.income === 'number' ? data.income : 0);
            const expense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : (typeof data.expense === 'number' ? data.expense : 0);

            yearTotals.income += income;
            yearTotals.expense += expense;
          }
        });
      }
    });

    // Convert to chart data format
    const chartData = Array.from(yearData.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([year, totals]) => ({
        year: year.toString(),
        'Geplante Einnahmen': totals.income,
        'Geplante Ausgaben': totals.expense,
        'Netto': totals.income - totals.expense
      }));

    console.log('Budget Chart Data:', chartData);
    return chartData;
  }, [budgetPlans]);

  // Chart-Daten für Kontoverlauf (gefiltert nach Jahr wenn Planung markiert)
  const accountChartData = useMemo(() => {
    if (!accountStatements.length || !accounts.length) return [];

    // Berechne Startbetrag aller Konten
    const totalStartbetrag = accounts.reduce((sum, account) => sum + (account.startbetrag || 0), 0);

    // Filtere nur echte Buchungen (keine Umbuchungen zwischen Konten)
    // Nur Buchungen wo konto_id gesetzt ist und von_konto_id sowie nach_konto_id null sind
    let filteredStatements = accountStatements.filter(stmt =>
      stmt.konto_id && !stmt.von_konto_id && !stmt.nach_konto_id
    );

    let baseOffset = 0;

    // Wenn eine Planung markiert ist, nur das Jahr der Planung zeigen
    if (planFilterYear) {
      // Berechne die Summe aller Beträge vor dem gefilterten Jahr
      const previousYearStatements = filteredStatements.filter(stmt => {
        const year = new Date(stmt.buchungstag).getFullYear();
        return year < planFilterYear;
      });

      // Basis-Offset = Startbetrag + Summe aller Beträge vor dem Jahr
      baseOffset = totalStartbetrag + previousYearStatements.reduce((sum, stmt) => sum + parseFloat(stmt.betrag), 0);

      // Jetzt filtere auf das gewünschte Jahr
      filteredStatements = filteredStatements.filter(stmt => {
        const year = new Date(stmt.buchungstag).getFullYear();
        return year === planFilterYear;
      });
    }

    void baseOffset;

    // Sortiere Statements chronologisch
    const sortedStatements = [...filteredStatements].sort((a, b) =>
      new Date(a.buchungstag).getTime() - new Date(b.buchungstag).getTime()
    );

    // Berechne akkumulierte Summe über alle Konten
    const monthlyData = new Map<string, { date: string, totalBetrag: number }>();
    let runningTotal = planFilterYear ? 0 : totalStartbetrag; // Start bei 0 wenn Jahr gefiltert, sonst bei Startbetrag

    sortedStatements.forEach(stmt => {
      const date = new Date(stmt.buchungstag);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      // Addiere den Betrag zur laufenden Summe (konvertiere String zu Number)
      runningTotal += parseFloat(stmt.betrag);

      // Speichere den akkumulierten Wert für diesen Monat (überschreibe bei mehreren Buchungen im selben Monat)
      monthlyData.set(monthKey, {
        date: monthKey,
        totalBetrag: runningTotal
      });
    });

    return Array.from(monthlyData.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(data => ({
        monat: data.date,
        'Kontosaldo': data.totalBetrag
      }));
  }, [accountStatements, planFilterYear, accounts]);

  // Chart-Daten für spezifische Kategorie (akkumuliert, gefiltert nach Jahr wenn Planung markiert)
  const categoryChartData = useMemo(() => {
    if (!selectedCategory || !categoryStatements.length) return [];

    let filteredStatements = categoryStatements;

    // Wenn eine Planung markiert ist, nur das Jahr der Planung zeigen
    if (planFilterYear) {
      filteredStatements = categoryStatements.filter(stmt => {
        const year = new Date(stmt.buchungstag).getFullYear();
        return year === planFilterYear;
      });
    }

    // Sortiere Statements chronologisch für korrekte Akkumulation
    const sortedStatements = [...filteredStatements].sort((a, b) =>
      new Date(a.buchungstag).getTime() - new Date(b.buchungstag).getTime()
    );

    const monthlyData = new Map<string, { date: string, amount: number }>();

    sortedStatements.forEach(stmt => {
      const date = new Date(stmt.buchungstag);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!monthlyData.has(monthKey)) {
        monthlyData.set(monthKey, { date: monthKey, amount: 0 });
      }

      // Summiere die Beträge aus den Kategorie-Zuordnungen für diese Kategorie
      if (stmt.categories && Array.isArray(stmt.categories)) {
        stmt.categories.forEach((cat: any) => {
          const categoryName = categories.find(c => c.id === cat.kategorie_id)?.name;
          if (categoryName === selectedCategory) {
            // Verwende das Vorzeichen der Hauptbuchung (cat.betrag ist immer positiv)
            const sign = parseFloat(stmt.betrag) >= 0 ? 1 : -1;
            const amount = parseFloat(cat.betrag) * sign;

            monthlyData.get(monthKey)!.amount += amount;
          }
        });
      }
    });

    // Akkumulierte Werte berechnen
    let accumulated = 0;
    const sortedMonths = Array.from(monthlyData.entries())
      .sort((a, b) => a[0].localeCompare(b[0]));

    return sortedMonths.map(([monthKey, data]) => {
      accumulated += data.amount;
      return {
        monat: monthKey,
        [`${selectedCategory}`]: accumulated
      };
    });
  }, [selectedCategory, categoryStatements, planFilterYear, categories]);

  // Kombinierte Chart-Daten - nutze optimierte Backend-Daten wenn verfügbar
  const combinedChartData = useMemo(() => {
    // Bevorzuge optimierte Backend-Daten (bereits vorberechnet)
    if (optimizedChartData.length > 0) {
      console.log('Verwende optimierte Chart-Daten vom Backend');
      return optimizedChartData;
    }

    // Fallback: Alte Frontend-Berechnung
    const baseData = accountChartData;

    if (selectedCategory && categoryChartData.length > 0) {
      let lastCategoryValue = 0;

      return baseData.map(item => {
        const categoryItem = categoryChartData.find(cat => cat.monat === item.monat);

        if (categoryItem) {
          const value = categoryItem[selectedCategory];
          lastCategoryValue = typeof value === 'number' ? value : 0;
        }

        return {
          ...item,
          [`${selectedCategory}`]: lastCategoryValue // Verwende letzten bekannten Wert statt 0
        };
      });
    }

    return baseData;
  }, [optimizedChartData, accountChartData, categoryChartData, selectedCategory]);

  // Handle Kategorie-Klick im Chart
  const handleCategoryClick = async (categoryName: string) => {
    if (selectedCategory === categoryName) {
      // Kategorie deselektieren
      setSelectedCategory(null);
      setCategoryStatements([]);
    } else {
      // Neue Kategorie auswählen
      setSelectedCategory(categoryName);
      await loadCategoryStatements(categoryName);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatCurrencyShort = (amount: number) => {
    if (Math.abs(amount) >= 1000) {
      return `${(amount / 1000).toFixed(1)}k €`;
    }
    return formatCurrency(amount);
  };

  // Stand / Datum
  const monthYear = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  // Featured-Plan: bevorzugt markierter Plan, sonst der höchste Jahr-Plan
  const featuredPlan: BudgetPlan | null = useMemo(() => {
    if (statistics.selectedPlan) return statistics.selectedPlan;
    if (budgetPlans.length === 0) return null;
    const sorted = [...budgetPlans].sort((a, b) => b.year - a.year);
    return sorted[0];
  }, [statistics.selectedPlan, budgetPlans]);

  // Plan-spezifische Stats (Featured / aktiver Plan)
  const featuredStats = useMemo(() => {
    if (!featuredPlan) {
      return {
        planIncome: 0,
        planExpense: 0,
        planNet: 0,
        istIncome: 0,
        istExpense: 0,
        istNet: 0,
        usagePct: 0,
        usageColor: '#34d399',
        topCategory: null as null | { name: string; value: number },
        overBudgetCount: 0,
        categories: [] as Array<{
          name: string;
          icon: string;
          planIncome: number;
          planExpense: number;
          istIncome: number;
          istExpense: number;
          comment?: string;
          usagePct: number;
        }>,
      };
    }

    // Geplante Werte
    const planIncome = Object.values(featuredPlan.budgetData).reduce((sum, data) => {
      const income = typeof data.income === 'string' ? parseFloat(data.income) || 0 : data.income || 0;
      return sum + income;
    }, 0);
    const planExpense = Object.values(featuredPlan.budgetData).reduce((sum, data) => {
      const expense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : data.expense || 0;
      return sum + expense;
    }, 0);
    const planNet = planIncome - planExpense;

    // Ist-Werte (echte Buchungen für Jahr)
    const yearFilteredStatements = accountStatements.filter(stmt => {
      const bookingYear = new Date(stmt.buchungstag).getFullYear();
      const isReal = stmt.konto_id && !stmt.von_konto_id && !stmt.nach_konto_id;
      return bookingYear === featuredPlan.year && isReal;
    });

    let istIncome = 0;
    let istExpense = 0;
    const istByCategory = new Map<string, { income: number; expense: number }>();

    yearFilteredStatements.forEach(stmt => {
      if (!stmt.categories) return;
      const isPositive = parseFloat(stmt.betrag) > 0;
      stmt.categories.forEach((cat: any) => {
        const catName = categories.find(c => c.id === cat.kategorie_id)?.name || 'Unbekannt';
        const amount = parseFloat(cat.betrag) || 0;
        const entry = istByCategory.get(catName) || { income: 0, expense: 0 };
        if (isPositive) {
          entry.income += amount;
          istIncome += amount;
        } else {
          entry.expense += amount;
          istExpense += amount;
        }
        istByCategory.set(catName, entry);
      });
    });

    const istNet = istIncome - istExpense;

    // Nutzung in % (Ausgaben gegen geplante Ausgaben)
    const usagePct = planExpense > 0 ? Math.round((istExpense / planExpense) * 100) : 0;
    const usageColor = usagePct <= 80 ? '#34d399' : usagePct <= 100 ? '#fbbf24' : '#fb7185';

    // Kategorien-Liste mit Plan und Ist
    const categoriesList = Object.entries(featuredPlan.budgetData).map(([name, data]) => {
      const cat = categories.find(c => c.name === name);
      const planIncome = typeof data.income === 'string' ? parseFloat(data.income) || 0 : data.income || 0;
      const planExpense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : data.expense || 0;
      const ist = istByCategory.get(name) || { income: 0, expense: 0 };
      const usagePct = planExpense > 0 ? Math.round((ist.expense / planExpense) * 100) : (ist.expense > 0 ? 999 : 0);
      return {
        name,
        icon: cat?.icon || 'default',
        planIncome,
        planExpense,
        istIncome: ist.income,
        istExpense: ist.expense,
        comment: data.comment,
        usagePct,
      };
    }).sort((a, b) => b.planExpense - a.planExpense);

    const topCategory = categoriesList[0]
      ? { name: categoriesList[0].name, value: categoriesList[0].planExpense }
      : null;
    const overBudgetCount = categoriesList.filter(c => c.usagePct > 100).length;

    return {
      planIncome,
      planExpense,
      planNet,
      istIncome,
      istExpense,
      istNet,
      usagePct,
      usageColor,
      topCategory,
      overBudgetCount,
      categories: categoriesList,
    };
  }, [featuredPlan, accountStatements, categories]);

  // Monatliche Auslastung — pro Monat: Ist-Ausgaben für aktiven Plan
  const monthlyUsage = useMemo(() => {
    if (!featuredPlan) return [] as Array<{ key: string; label: string; year: number; expense: number; income: number }>;

    const months: Array<{ key: string; label: string; year: number; expense: number; income: number }> = [];
    for (let m = 0; m < 12; m++) {
      const dt = new Date(featuredPlan.year, m, 1);
      months.push({
        key: `${featuredPlan.year}-${String(m + 1).padStart(2, '0')}`,
        label: dt.toLocaleDateString('de-DE', { month: 'short' }),
        year: featuredPlan.year,
        expense: 0,
        income: 0,
      });
    }

    accountStatements.forEach(stmt => {
      const date = new Date(stmt.buchungstag);
      if (date.getFullYear() !== featuredPlan.year) return;
      if (!stmt.konto_id || stmt.von_konto_id || stmt.nach_konto_id) return;
      const idx = date.getMonth();
      if (!stmt.categories) return;
      const isPositive = parseFloat(stmt.betrag) > 0;
      stmt.categories.forEach((cat: any) => {
        const amount = parseFloat(cat.betrag) || 0;
        if (isPositive) months[idx].income += amount;
        else months[idx].expense += amount;
      });
    });

    return months;
  }, [featuredPlan, accountStatements]);

  // Story-Snippets dynamisch
  const renderHeroStory = () => {
    if (!featuredPlan) {
      return (
        <>
          Lege deine erste <span className="font-bold text-primary-700 dark:text-primary-300">Budget-Planung</span> an, um dem Verein einen klaren Finanzkurs zu geben.
        </>
      );
    }
    const parts: React.ReactNode[] = [];
    parts.push(
      <React.Fragment key="head">
        <span className="font-bold text-gray-900 dark:text-white">{featuredPlan.name}</span>{' '}
        plant <span className="font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(featuredStats.planIncome)} Einnahmen</span>{' '}
        gegen <span className="font-bold text-rose-700 dark:text-rose-300">{formatCurrency(featuredStats.planExpense)} Ausgaben</span>
      </React.Fragment>
    );
    if (featuredStats.topCategory) {
      parts.push(
        <React.Fragment key="top-cat">
          {' · größte Position '}
          <span className="font-bold text-gray-900 dark:text-white">{featuredStats.topCategory.name}</span>
        </React.Fragment>
      );
    }
    if (featuredStats.overBudgetCount > 0) {
      parts.push(
        <React.Fragment key="over-budget">
          {' · '}
          <span className="font-bold text-rose-700 dark:text-rose-300">{featuredStats.overBudgetCount} Über-Budget</span>
        </React.Fragment>
      );
    }
    return <>{parts}.</>;
  };

  if (!isAuthorized) {
    return (
      <div
        className="min-h-screen font-sans text-gray-800 dark:text-slate-200"
        style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' } as React.CSSProperties}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">
          <section className="pt-2 pb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-rose-400/80 font-semibold mb-3">
              Zugriff verweigert
            </div>
            <h2
              className="text-4xl md:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.02]"
              style={{ letterSpacing: '-0.04em' }}
            >
              Nur für<br /><span className="text-rose-600 dark:text-rose-400">Vorstand.</span>
            </h2>
            <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
              Diese Seite ist nur für Vorstandsmitglieder zugänglich.
            </p>
          </section>
        </div>
      </div>
    );
  }

  // Bar-Chart Werte vorbereiten
  const monthlyMaxExpense = monthlyUsage.length
    ? Math.max(...monthlyUsage.map(m => m.expense), 1)
    : 1;
  const monthlyMaxIncome = monthlyUsage.length
    ? Math.max(...monthlyUsage.map(m => m.income), 1)
    : 1;

  const usageDeg = (Math.min(featuredStats.usagePct, 100) / 100) * 360;

  return (
    <div
      className="min-h-screen font-sans text-gray-800 dark:text-slate-200"
      style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' } as React.CSSProperties}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

        {/* INTRO-Schriftzug */}
        <section className="pt-2 pb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-info-400/80 font-semibold mb-3">
            Budget · Stand {monthYear}
          </div>
          <h2
            className="text-4xl md:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.02]"
            style={{ letterSpacing: '-0.04em' }}
          >
            Vereins-<br />
            <span className="text-info-600 dark:text-info-400">Plan.</span>
          </h2>

          {featuredPlan ? (
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-start">
              {/* Restbudget (links, groß) */}
              <div className="lg:min-w-[280px]">
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-slate-500 font-bold">
                  Restbudget {featuredPlan.year}
                </div>
                {(() => {
                  const remaining = featuredStats.planExpense - featuredStats.istExpense;
                  const tone = featuredStats.usagePct > 100
                    ? { main: 'text-rose-700 dark:text-rose-300', sub: 'text-rose-700/70 dark:text-rose-300/70' }
                    : featuredStats.usagePct > 80
                    ? { main: 'text-amber-700 dark:text-amber-300', sub: 'text-amber-700/70 dark:text-amber-300/70' }
                    : { main: 'text-emerald-700 dark:text-emerald-300', sub: 'text-emerald-700/70 dark:text-emerald-300/70' };
                  const sign = remaining >= 0 ? '' : '−';
                  const [intPart, restPart] = formatCurrency(Math.abs(remaining)).split(',');
                  return (
                    <>
                      <div className={`text-5xl md:text-7xl font-black tabular-nums leading-none mt-2 ${tone.main}`}>
                        {sign}{intPart}
                        <span className={`text-3xl md:text-4xl ${tone.sub}`}>,{restPart}</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-slate-500 mt-3 tabular-nums">
                        {featuredStats.usagePct}% verbraucht · {formatCurrency(featuredStats.istExpense)} von {formatCurrency(featuredStats.planExpense)}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Prosa (rechts) */}
              <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
                {renderHeroStory()}
              </p>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
              {renderHeroStory()}
            </p>
          )}
        </section>

        {/* CTA-Banner: Neue Planung */}
        <section className="pb-6">
          <div
            className="rounded-3xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
          >
            <div className="flex items-center gap-4 flex-1 min-w-0 w-full">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)' }}
              >
                <CurrencyEuroIcon className="w-6 h-6 text-primary-700 dark:text-primary-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Budget-Planung</div>
                <h4 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
                  {budgetPlans.length === 0 ? 'Noch keine Planung angelegt' : `${budgetPlans.length} ${budgetPlans.length === 1 ? 'Planung' : 'Planungen'} im System`}
                </h4>
                <p className="text-gray-600 dark:text-slate-400 text-xs sm:text-sm mt-1">
                  {budgetPlans.length === 0
                    ? 'Lege jetzt eine Planung an, um Ist- gegen Soll-Werte zu vergleichen.'
                    : 'Klicke auf eine Planung unten zum Markieren · neue Planung anlegen.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950 shrink-0"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Neue Planung</span>
            </button>
          </div>
        </section>

        {/* HERO Featured-Card */}
        {featuredPlan && (
          <section className="pb-6">
            <article
              className="relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div
                className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none"
                style={{
                  background: featuredStats.usagePct > 100
                    ? 'rgba(244,63,94,0.10)'
                    : featuredStats.usagePct > 80
                    ? 'rgba(245,158,11,0.10)'
                    : 'rgba(16,185,129,0.10)',
                }}
              />
              <div className="relative px-6 md:px-10 py-8 md:py-10">
                <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span
                        className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold"
                        style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                      >
                        Aktive Periode
                      </span>
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        style={{ color: '#cbd5e1'  }}
                      >
                        <CalendarIcon className="w-3.5 h-3.5" />
                        Jahr {featuredPlan.year}
                      </span>
                      {featuredStats.overBudgetCount > 0 && (
                        <span
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold"
                          style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                          {featuredStats.overBudgetCount} Über-Budget
                        </span>
                      )}
                    </div>
                    <h3
                      className="text-gray-900 dark:text-white font-black leading-none"
                      style={{
                        fontSize: 'clamp(2rem, 5vw, 3.75rem)',
                        letterSpacing: '-0.04em',
                      }}
                    >
                      {featuredPlan.name}
                    </h3>
                    <p className="text-gray-700 dark:text-slate-300 text-sm md:text-base leading-relaxed mt-4 max-w-xl">
                      <span className="font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(featuredStats.istIncome)}</span> Einnahmen{' / '}
                      <span className="text-gray-600 dark:text-slate-400">{formatCurrency(featuredStats.planIncome)} Plan</span>
                      {' · '}
                      <span className="font-bold text-rose-700 dark:text-rose-300">{formatCurrency(featuredStats.istExpense)}</span> Ausgaben{' / '}
                      <span className="text-gray-600 dark:text-slate-400">{formatCurrency(featuredStats.planExpense)} Plan</span>
                      {' · Netto '}
                      <span className={`font-bold ${featuredStats.istNet >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>
                        {formatCurrency(featuredStats.istNet)}
                      </span>
                      .
                    </p>

                    <div className="mt-6 flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setSelectedBudgetPlan(featuredPlan);
                          setShowDetailModal(true);
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950"
                      >
                        <EyeIcon className="w-4 h-4" />
                        Details öffnen
                      </button>
                      {markedPlanId !== featuredPlan.id ? (
                        <button
                          onClick={() => {
                            setMarkedPlanId(featuredPlan.id);
                            setPlanFilterYear(featuredPlan.year);
                            setSelectedCategory(null);
                            setCategoryStatements([]);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10"
                        >
                          <CheckIcon className="w-4 h-4" />
                          Markieren
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setMarkedPlanId(null);
                            setPlanFilterYear(null);
                            setSelectedCategory(null);
                            setCategoryStatements([]);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition"
                          style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                        >
                          <CheckIcon className="w-4 h-4" />
                          Markiert
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Conic ring: Verbraucht */}
                  <div className="shrink-0 mx-auto md:mx-0">
                    <div
                      className="relative w-32 h-32 rounded-full"
                      style={{
                        background: `conic-gradient(from 0deg, ${featuredStats.usageColor} 0deg ${usageDeg}deg, rgba(255,255,255,0.08) ${usageDeg}deg 360deg)`,
                        padding: 5,
                      }}
                    >
                      <div
                        className="w-full h-full rounded-full flex flex-col items-center justify-center bg-white dark:bg-[#1c2432]"
                        style={{ border: '1px solid #1e293b'  }}
                      >
                        <div className="text-[9px] uppercase tracking-widest text-gray-600 dark:text-slate-400">Verbraucht</div>
                        <div className="text-3xl font-black text-gray-900 dark:text-white leading-none tabular-nums">
                          {featuredStats.usagePct}<span className="text-base text-gray-500 dark:text-slate-500 font-bold">%</span>
                        </div>
                        <div className="text-[10px] font-semibold mt-1" style={{ color: featuredStats.usageColor }}>
                          {featuredStats.usagePct > 100 ? 'über Plan' : featuredStats.usagePct >= 90 ? 'fast voll' : 'im Plan'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer-Breakdown */}
              <div className="md:hidden relative px-5 py-5" style={{ borderTop: '1px solid #1e293b' }}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Geplant Netto</div>
                    <div className={`text-xl font-black tabular-nums mt-0.5 ${featuredStats.planNet >= 0 ? 'text-primary-300' : 'text-amber-300'}`}>
                      {formatCurrency(featuredStats.planNet)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Ist Netto</div>
                    <div className={`text-xl font-black tabular-nums mt-0.5 ${featuredStats.istNet >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>
                      {formatCurrency(featuredStats.istNet)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Kategorien</div>
                    <div className="text-xl font-black text-gray-900 dark:text-white tabular-nums mt-0.5">{featuredStats.categories.length}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Top-Kategorie</div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white truncate mt-0.5">
                      {featuredStats.topCategory?.name || '—'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden md:block relative px-8 py-6" style={{ borderTop: '1px solid #1e293b' }}>
                <div className="grid grid-cols-4 gap-6 items-center">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Geplant Netto</div>
                    <div className={`text-2xl font-black tabular-nums mt-1 ${featuredStats.planNet >= 0 ? 'text-primary-300' : 'text-amber-300'}`}>
                      {formatCurrency(featuredStats.planNet)}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-slate-500 mt-0.5">Erwartet</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Ist Netto</div>
                    <div className={`text-2xl font-black tabular-nums mt-1 ${featuredStats.istNet >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>
                      {formatCurrency(featuredStats.istNet)}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-slate-500 mt-0.5">Live aus Buchungen</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Top-Kategorie</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white truncate mt-1">{featuredStats.topCategory?.name || '—'}</div>
                    <div className="text-[10px] text-gray-500 dark:text-slate-500 mt-0.5">
                      {featuredStats.topCategory ? `${formatCurrency(featuredStats.topCategory.value)} geplant` : 'keine Daten'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Kategorien</div>
                    <div className="text-2xl font-black text-gray-900 dark:text-white tabular-nums mt-1">{featuredStats.categories.length}</div>
                    <div className="text-[10px] text-gray-500 dark:text-slate-500 mt-0.5">{featuredStats.overBudgetCount} über Budget</div>
                  </div>
                </div>
              </div>
            </article>
          </section>
        )}

        {/* TABS */}
        <section className="pb-6">
          <div
            className="inline-flex p-1 rounded-2xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
          >
            <button
              onClick={() => setActiveTab('overview')}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                activeTab === 'overview' ? 'bg-primary-500 text-slate-950' : 'text-slate-400 hover:text-white'
              }`}
            >
              Übersicht
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                  activeTab === 'overview' ? 'bg-slate-950/30 text-slate-950' : 'bg-white/10 text-white'
                }`}
              >
                {budgetPlans.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                activeTab === 'categories' ? 'bg-primary-500 text-slate-950' : 'text-slate-400 hover:text-white'
              }`}
            >
              Pro Kategorie
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                  activeTab === 'categories' ? 'bg-slate-950/30 text-slate-950' : 'bg-white/10 text-white'
                }`}
              >
                {featuredStats.categories.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                activeTab === 'history' ? 'bg-primary-500 text-slate-950' : 'text-slate-400 hover:text-white'
              }`}
            >
              Verlauf
            </button>
          </div>
        </section>

        {/* TAB: ÜBERSICHT — Plan-Liste als Story-Rows */}
        {activeTab === 'overview' && (
          <section className="pb-6">
            {loading ? (
              <article
                className="rounded-3xl p-10 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              >
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400 mx-auto" />
                <p className="text-gray-600 dark:text-slate-400 mt-3 text-sm">Lade Budget-Planungen...</p>
              </article>
            ) : budgetPlans.length === 0 ? (
              /* EMPTY STATE */
              <div
                className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
              >
                <div
                  className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                >
                  <DocumentTextIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
                </div>
                <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                  Keine Budget-Planungen
                </h4>
                <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                  Erstelle deine erste Planung als Soll-Vorgabe — die App vergleicht laufend mit den echten Buchungen.
                </p>
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold bg-primary-500 hover:bg-primary-400 text-slate-950 transition"
                  >
                    <PlusIcon className="w-5 h-5" />
                    Erste Planung erstellen
                  </button>
                </div>
              </div>
            ) : (
              <article
                className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
              >
                <div
                  className="px-5 sm:px-6 py-4 flex items-start justify-between gap-3"
                  style={{ borderBottom: '1px solid #1e293b' }}
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Planungen</div>
                    <h4 className="text-lg md:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                      Alle Budget-Pläne
                    </h4>
                  </div>
                </div>

                <div>
                  {budgetPlans.map((plan, idx) => {
                    const planIncome = Object.values(plan.budgetData).reduce((sum, data) => {
                      const income = typeof data.income === 'string' ? parseFloat(data.income) || 0 : data.income || 0;
                      return sum + income;
                    }, 0);

                    const planExpense = Object.values(plan.budgetData).reduce((sum, data) => {
                      const expense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : data.expense || 0;
                      return sum + expense;
                    }, 0);

                    const planNet = planIncome - planExpense;

                    const yearFilteredStatements = accountStatements.filter(stmt => {
                      const bookingYear = new Date(stmt.buchungstag).getFullYear();
                      const matchesYear = bookingYear === plan.year;
                      const isRealBooking = stmt.konto_id && !stmt.von_konto_id && !stmt.nach_konto_id;
                      return matchesYear && isRealBooking;
                    });

                    const istIncome = yearFilteredStatements.reduce((sum, stmt) => {
                      if (stmt.categories && stmt.betrag > 0) {
                        stmt.categories.forEach((cat: any) => {
                          sum += cat.betrag;
                        });
                      }
                      return sum;
                    }, 0);

                    const istExpense = yearFilteredStatements.reduce((sum, stmt) => {
                      if (stmt.categories && stmt.betrag < 0) {
                        stmt.categories.forEach((cat: any) => {
                          sum += cat.betrag;
                        });
                      }
                      return sum;
                    }, 0);

                    const istNet = istIncome - istExpense;
                    const usagePct = planExpense > 0 ? Math.round((istExpense / planExpense) * 100) : 0;
                    const isOver = usagePct > 100;
                    const isWarn = usagePct > 80 && usagePct <= 100;
                    const tone = isOver ? 'rose' : isWarn ? 'amber' : 'primary';

                    const isMarked = markedPlanId === plan.id;

                    // Date-Tile-Style Year-Tile
                    const yearTileBg =
                      tone === 'rose'
                        ? 'linear-gradient(180deg, rgba(244,63,94,0.16) 0%, rgba(244,63,94,0.04) 100%)'
                        : tone === 'amber'
                        ? 'linear-gradient(180deg, rgba(245,158,11,0.16) 0%, rgba(245,158,11,0.04) 100%)'
                        : 'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)';
                    const yearTileBorder =
                      tone === 'rose'
                        ? '1px solid rgba(244,63,94,0.25)'
                        : tone === 'amber'
                        ? '1px solid rgba(245,158,11,0.25)'
                        : '1px solid rgba(16,185,129,0.25)';
                    const yearTileLabel =
                      tone === 'rose' ? 'text-rose-300' : tone === 'amber' ? 'text-amber-300' : 'text-primary-300';

                    return (
                      <div
                        key={plan.id}
                        className="px-5 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 transition cursor-pointer"
                        style={{
                          borderBottom: idx < budgetPlans.length - 1 ? '1px solid #1e293b' : 'none',
                          background: isMarked ? 'rgba(16,185,129,0.05)' : 'transparent',
                        }}
                        onMouseEnter={(e) => {
                          if (!isMarked) e.currentTarget.style.background = 'rgba(255,255,255,0.025)';
                        }}
                        onMouseLeave={(e) => {
                          if (!isMarked) e.currentTarget.style.background = 'transparent';
                        }}
                        onClick={() => {
                          if (markedPlanId === plan.id) {
                            setMarkedPlanId(null);
                            setPlanFilterYear(null);
                          } else {
                            setMarkedPlanId(plan.id);
                            setPlanFilterYear(plan.year);
                          }
                          setSelectedCategory(null);
                          setCategoryStatements([]);
                        }}
                      >
                        {/* Year-Tile */}
                        <div
                          className="flex flex-col items-center justify-center shrink-0"
                          style={{ width: 64, height: 76, borderRadius: 14, background: yearTileBg, border: yearTileBorder }}
                        >
                          <div className={`text-[10px] uppercase tracking-widest font-bold ${yearTileLabel}`}>Jahr</div>
                          <div className="text-2xl font-black text-gray-900 dark:text-white leading-none mt-0.5 tabular-nums">{plan.year}</div>
                          <div className={`text-[10px] mt-0.5 ${yearTileLabel}`}>{usagePct}%</div>
                        </div>

                        {/* Center */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {isMarked && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                                style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                              >
                                <CheckIcon className="w-3 h-3" /> Markiert
                              </span>
                            )}
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                              style={
                                tone === 'rose'
                                  ? { background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }
                                  : tone === 'amber'
                                  ? { background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', color: '#fcd34d' }
                                  : { background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }
                              }
                            >
                              {tone === 'rose' ? (
                                <>
                                  <ExclamationTriangleIcon className="w-3 h-3" /> Über Budget
                                </>
                              ) : tone === 'amber' ? (
                                <>
                                  <ExclamationTriangleIcon className="w-3 h-3" /> Knapp
                                </>
                              ) : (
                                <>
                                  <CheckCircleIcon className="w-3 h-3" /> Im Plan
                                </>
                              )}
                            </span>
                          </div>
                          <div className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">{plan.name}</div>
                          <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                            <span className="text-emerald-700 dark:text-emerald-300">{formatCurrencyShort(istIncome)}</span>
                            {' / '}
                            <span className="text-gray-500 dark:text-slate-500">{formatCurrencyShort(planIncome)}</span>
                            <span className="text-slate-600"> · </span>
                            <span className="text-rose-700 dark:text-rose-300">{formatCurrencyShort(istExpense)}</span>
                            {' / '}
                            <span className="text-gray-500 dark:text-slate-500">{formatCurrencyShort(planExpense)}</span>
                          </div>

                          {/* Progress-Bar Ausgaben */}
                          <div
                            className="mt-2 h-1.5 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                          >
                            <div
                              className="absolute left-0 top-0 h-full rounded-full transition-all"
                              style={{
                                width: `${Math.min(usagePct, 100)}%`,
                                background:
                                  tone === 'rose'
                                    ? 'linear-gradient(90deg, rgba(244,63,94,0.55) 0%, rgba(244,63,94,0.30) 100%)'
                                    : tone === 'amber'
                                    ? 'linear-gradient(90deg, rgba(245,158,11,0.55) 0%, rgba(245,158,11,0.30) 100%)'
                                    : 'linear-gradient(90deg, rgba(16,185,129,0.55) 0%, rgba(16,185,129,0.30) 100%)',
                              }}
                            />
                          </div>
                        </div>

                        {/* Desktop-Stats */}
                        <div className="hidden md:flex items-end gap-5 shrink-0">
                          <div className="text-right">
                            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Netto Ist</div>
                            <div className={`text-base font-black tabular-nums ${istNet >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>
                              {formatCurrencyShort(istNet)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Plan</div>
                            <div className="text-base font-black text-gray-900 dark:text-white tabular-nums">
                              {formatCurrencyShort(planNet)}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-1 shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBudgetPlan(plan);
                              setShowDetailModal(true);
                            }}
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-white/5 hover:text-white transition"
                            title="Details"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBudgetPlan(plan);
                            }}
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-rose-500/10 hover:text-rose-300 transition"
                            title="Löschen"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            )}
          </section>
        )}

        {/* TAB: PRO KATEGORIE */}
        {activeTab === 'categories' && featuredPlan && (
          <section className="pb-6 space-y-6">
            <article
              className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div
                className="px-5 sm:px-6 py-4 flex items-start justify-between gap-3 flex-wrap"
                style={{ borderBottom: '1px solid #1e293b' }}
              >
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Plan {featuredPlan.year}</div>
                  <h4 className="text-lg md:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Kategorien-Auslastung
                  </h4>
                  <p className="text-gray-600 dark:text-slate-400 text-xs mt-1">
                    Sortiert nach geplanten Ausgaben · klick eine Kategorie für Detail-Chart unten.
                  </p>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-gray-600 dark:text-slate-400">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-primary-400" />
                    Im Plan
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    Knapp
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                    Über Budget
                  </span>
                </div>
              </div>

              {featuredStats.categories.length === 0 ? (
                <div className="p-10 text-center text-sm text-gray-600 dark:text-slate-400">
                  Keine Kategorien in diesem Plan.
                </div>
              ) : (
                <div>
                  {featuredStats.categories.map((cat, idx) => {
                    const Icon = getIconForCategory(cat.icon);
                    const usage = cat.usagePct;
                    const tone = usage > 100 ? 'rose' : usage > 80 ? 'amber' : 'primary';
                    const barColor =
                      tone === 'rose'
                        ? 'linear-gradient(90deg, rgba(244,63,94,0.55) 0%, rgba(244,63,94,0.25) 100%)'
                        : tone === 'amber'
                        ? 'linear-gradient(90deg, rgba(245,158,11,0.55) 0%, rgba(245,158,11,0.25) 100%)'
                        : 'linear-gradient(90deg, rgba(16,185,129,0.55) 0%, rgba(16,185,129,0.25) 100%)';
                    const labelColor =
                      tone === 'rose' ? 'text-rose-300' : tone === 'amber' ? 'text-amber-300' : 'text-primary-300';
                    const isSelected = selectedCategory === cat.name;

                    return (
                      <div
                        key={cat.name}
                        className="px-5 sm:px-6 py-4 transition cursor-pointer"
                        style={{
                          borderBottom: idx < featuredStats.categories.length - 1 ? '1px solid #1e293b' : 'none',
                          background: isSelected ? 'rgba(16,185,129,0.05)' : 'transparent',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.025)';
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) e.currentTarget.style.background = 'transparent';
                        }}
                        onClick={() => handleCategoryClick(cat.name)}
                      >
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                            style={{
                              background:
                                tone === 'rose'
                                  ? 'rgba(244,63,94,0.10)'
                                  : tone === 'amber'
                                  ? 'rgba(245,158,11,0.10)'
                                  : 'rgba(16,185,129,0.10)',
                              border:
                                tone === 'rose'
                                  ? '1px solid rgba(244,63,94,0.30)'
                                  : tone === 'amber'
                                  ? '1px solid rgba(245,158,11,0.30)'
                                  : '1px solid rgba(16,185,129,0.30)',
                            }}
                          >
                            <Icon
                              className={`w-5 h-5 ${
                                tone === 'rose' ? 'text-rose-400' : tone === 'amber' ? 'text-amber-400' : 'text-primary-400'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <div className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">{cat.name}</div>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${labelColor}`}
                                style={{
                                  background:
                                    tone === 'rose'
                                      ? 'rgba(244,63,94,0.10)'
                                      : tone === 'amber'
                                      ? 'rgba(245,158,11,0.10)'
                                      : 'rgba(16,185,129,0.10)',
                                  border:
                                    tone === 'rose'
                                      ? '1px solid rgba(244,63,94,0.30)'
                                      : tone === 'amber'
                                      ? '1px solid rgba(245,158,11,0.30)'
                                      : '1px solid rgba(16,185,129,0.30)',
                                }}
                              >
                                {usage > 100 ? `${usage}% — Über Budget` : `${usage}%`}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-slate-400 truncate">
                              <span className="text-rose-700 dark:text-rose-300 font-bold tabular-nums">{formatCurrencyShort(cat.istExpense)}</span>
                              {' / '}
                              <span className="text-gray-500 dark:text-slate-500 tabular-nums">{formatCurrencyShort(cat.planExpense)}</span>
                              {cat.planIncome > 0 && (
                                <>
                                  <span className="text-slate-600"> · Einnahmen </span>
                                  <span className="text-emerald-700 dark:text-emerald-300 font-bold tabular-nums">{formatCurrencyShort(cat.istIncome)}</span>
                                  {' / '}
                                  <span className="text-gray-500 dark:text-slate-500 tabular-nums">{formatCurrencyShort(cat.planIncome)}</span>
                                </>
                              )}
                            </div>
                            {cat.comment && (
                              <div className="text-[11px] text-gray-500 dark:text-slate-500 italic mt-0.5 truncate">{cat.comment}</div>
                            )}

                            {/* Progress-Bar */}
                            <div
                              className="mt-2 h-2 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                            >
                              <div
                                className="absolute left-0 top-0 h-full rounded-full transition-all"
                                style={{
                                  width: `${Math.min(usage, 100)}%`,
                                  background: barColor,
                                }}
                              />
                              {usage > 100 && (
                                <div
                                  className="absolute right-0 top-0 h-full rounded-r-full"
                                  style={{
                                    width: `${Math.min(usage - 100, 100)}%`,
                                    background:
                                      'repeating-linear-gradient(45deg, rgba(244,63,94,0.55) 0px, rgba(244,63,94,0.55) 4px, rgba(244,63,94,0.30) 4px, rgba(244,63,94,0.30) 8px)',
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </article>

            {/* Available Account-Categories with no plan */}
            {accountCategories.length > 0 && (
              <article
                className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              >
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Konto-Kategorien</div>
                  <h4 className="text-lg md:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Live-Kategorien aus Buchungen
                  </h4>
                  <p className="text-gray-600 dark:text-slate-400 text-xs mt-1">
                    Klicke eine Kategorie zum Filtern im Verlauf-Tab.
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                  {accountCategories
                    .filter(c => c.transactionCount > 0 || c.income > 0 || c.expense > 0)
                    .slice(0, 30)
                    .map((cat) => {
                      const matched = categories.find(c => c.name === cat.category);
                      const Icon = getIconForCategory(matched?.icon || cat.icon || 'default');
                      const net = cat.income - cat.expense;
                      const isSelected = selectedCategory === cat.category;
                      return (
                        <button
                          key={cat.category}
                          onClick={() => handleCategoryClick(cat.category)}
                          className="px-3 py-2.5 rounded-xl text-left transition"
                          style={
                            isSelected
                              ? {
                                  background: 'rgba(16,185,129,0.10)',
                                  border: '1px solid rgba(16,185,129,0.40)',
                                }
                              : {
                                  background: 'rgba(15,23,42,0.4)',
                                  border: '1px solid rgba(51,65,85,0.6)',
                                }
                          }
                        >
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${isSelected ? 'text-primary-300' : 'text-slate-400'}`} />
                            <span className="text-xs font-bold text-gray-900 dark:text-white truncate">{cat.category}</span>
                          </div>
                          <div className={`text-xs font-black tabular-nums mt-1 ${net >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>
                            {formatCurrencyShort(net)}
                          </div>
                        </button>
                      );
                    })}
                </div>
              </article>
            )}
          </section>
        )}

        {activeTab === 'categories' && !featuredPlan && (
          <section className="pb-6">
            <div
              className="rounded-3xl p-10 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              <p className="text-gray-600 dark:text-slate-400">Lege zuerst eine Budget-Planung an.</p>
            </div>
          </section>
        )}

        {/* TAB: VERLAUF */}
        {activeTab === 'history' && (
          <section className="pb-6 space-y-6">
            {/* Saison-Verlauf — 12 Monate Bar-Tiles */}
            {featuredPlan && monthlyUsage.length > 0 && (
              <article
                className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
              >
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Monatliche Auslastung</div>
                    <h4 className="text-lg md:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                      {featuredPlan.year} · 12 Monate
                    </h4>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-gray-600 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                      Einnahmen
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                      Ausgaben
                    </span>
                  </div>
                </div>

                {/* Horizontale Bar-Charts pro Monat */}
                <div className="space-y-2">
                  {monthlyUsage.map(m => {
                    const expensePct = (m.expense / monthlyMaxExpense) * 100;
                    const incomePct = (m.income / monthlyMaxIncome) * 100;
                    const empty = m.expense === 0 && m.income === 0;
                    return (
                      <div key={m.key} className="grid grid-cols-[60px_1fr] gap-3 items-center">
                        <span className="text-[11px] text-gray-600 dark:text-slate-400 tabular-nums uppercase tracking-widest font-bold">
                          {m.label}
                        </span>
                        <div className="space-y-1">
                          {empty ? (
                            <div className="h-5 rounded-full" style={{ background: 'rgba(255,255,255,0.025)' }} />
                          ) : (
                            <>
                              {m.income > 0 && (
                                <div
                                  className="h-5 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                                >
                                  <div
                                    className="absolute left-0 top-0 h-full rounded-full transition-all"
                                    style={{
                                      width: `${incomePct}%`,
                                      background:
                                        'linear-gradient(90deg, rgba(16,185,129,0.50) 0%, rgba(16,185,129,0.20) 100%)',
                                      borderRight: '1px solid rgba(16,185,129,0.50)',
                                    }}
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-900 dark:text-white z-10 tabular-nums">
                                    +{formatCurrencyShort(m.income)}
                                  </span>
                                </div>
                              )}
                              {m.expense > 0 && (
                                <div
                                  className="h-5 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                                >
                                  <div
                                    className="absolute left-0 top-0 h-full rounded-full transition-all"
                                    style={{
                                      width: `${expensePct}%`,
                                      background:
                                        'linear-gradient(90deg, rgba(244,63,94,0.50) 0%, rgba(244,63,94,0.20) 100%)',
                                      borderRight: '1px solid rgba(244,63,94,0.50)',
                                    }}
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-900 dark:text-white z-10 tabular-nums">
                                    −{formatCurrencyShort(m.expense)}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            )}

            {/* Kontoverlauf-Linie (Recharts) */}
            <article
              className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
            >
              <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Kontosaldo · Linie</div>
                  <h4 className="text-lg md:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Verlauf {planFilterYear ? `· ${planFilterYear}` : '· alle Jahre'}
                  </h4>
                  {selectedCategory && (
                    <p className="text-amber-700 dark:text-amber-300 text-xs mt-1">
                      Aktive Kategorie: <span className="font-bold">{selectedCategory}</span>
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setShowAdvancedChart(!showAdvancedChart)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10"
                >
                  <ChartBarIcon className="w-4 h-4" />
                  {showAdvancedChart ? 'Ausblenden' : 'Anzeigen'}
                </button>
              </div>

              {showAdvancedChart && combinedChartData.length > 0 ? (
                <div style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={combinedChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="monat"
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                        tickLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                        tickFormatter={(value) => {
                          if (typeof value === 'string' && value.includes('-')) {
                            const [year, month] = value.split('-');
                            return `${month}/${year.slice(-2)}`;
                          }
                          return value;
                        }}
                      />
                      <YAxis
                        yAxisId="left"
                        tickFormatter={(value) => {
                          const absValue = Math.abs(value);
                          if (absValue >= 1000) return `${(value / 1000).toFixed(1)}k`;
                          return value.toFixed(0);
                        }}
                        tick={{ fontSize: 11, fill: '#60a5fa' }}
                        axisLine={{ stroke: 'rgba(96,165,250,0.4)' }}
                        tickLine={{ stroke: 'rgba(96,165,250,0.4)' }}
                        width={50}
                      />
                      {selectedCategory && (
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tickFormatter={(value) => {
                            const absValue = Math.abs(value);
                            if (absValue >= 1000) return `${(value / 1000).toFixed(1)}k`;
                            return value.toFixed(0);
                          }}
                          tick={{ fontSize: 11, fill: '#fbbf24' }}
                          axisLine={{ stroke: 'rgba(251,191,36,0.4)' }}
                          tickLine={{ stroke: 'rgba(251,191,36,0.4)' }}
                          width={50}
                        />
                      )}
                      <RechartsTooltip
                        formatter={(value: number) => formatCurrency(value)}
                        contentStyle={{
                          background: '#0f172a',
                          border: '1px solid #334155',
                          borderRadius: 12,
                          color: '#e2e8f0',
                        }}
                      />
                      <RechartsLegend
                        wrapperStyle={{ color: '#cbd5e1', fontSize: 12 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="Kontosaldo"
                        stroke="#60a5fa"
                        strokeWidth={3}
                        dot={{ fill: '#60a5fa', strokeWidth: 2, r: 4 }}
                        name="Gesamtkontosaldo"
                      />
                      {selectedCategory && (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey={selectedCategory}
                          stroke="#fbbf24"
                          strokeWidth={2}
                          dot={{ fill: '#fbbf24', strokeWidth: 2, r: 3 }}
                          name={`${selectedCategory} (Buchungen)`}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-10">
                  <ChartBarIcon className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    {showAdvancedChart ? 'Kontodaten werden geladen oder fehlen.' : 'Chart ausgeblendet.'}
                  </p>
                </div>
              )}
            </article>

            {/* Kategorie-Picker für den Linien-Chart */}
            <article
              className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Kategorie-Overlay</div>
                <h4 className="text-lg md:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                  Detail im Chart anzeigen
                </h4>
                <p className="text-gray-600 dark:text-slate-400 text-xs mt-1">
                  Wähle eine Kategorie für die zweite Linie im Verlauf.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                {categories.map((category) => {
                  const Icon = getIconForCategory(category.icon);
                  const cd = accountCategories.find(c => c.category === category.name);
                  const hasBookings = cd && (cd.transactionCount > 0 || cd.income > 0 || cd.expense > 0);
                  const isSelected = selectedCategory === category.name;
                  const net = cd ? cd.income - cd.expense : 0;
                  return (
                    <button
                      key={category.id}
                      disabled={!hasBookings}
                      onClick={() => hasBookings && handleCategoryClick(category.name)}
                      className="px-3 py-2.5 rounded-xl text-left transition disabled:opacity-40 disabled:cursor-not-allowed"
                      style={
                        isSelected
                          ? {
                              background: 'rgba(245,158,11,0.10)',
                              border: '1px solid rgba(245,158,11,0.40)',
                            }
                          : {
                              background: 'rgba(15,23,42,0.4)',
                              border: '1px solid rgba(51,65,85,0.6)',
                            }
                      }
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className={`w-4 h-4 shrink-0 ${isSelected ? 'text-amber-300' : 'text-slate-400'}`} />
                        <span className="text-xs font-bold text-gray-900 dark:text-white truncate">{category.name}</span>
                      </div>
                      {hasBookings && (
                        <div className={`text-xs font-black tabular-nums mt-1 ${net >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>
                          {formatCurrencyShort(net)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </article>

            {/* Jahresvergleich Bar-Chart */}
            {budgetChartData.length > 0 && (
              <article
                className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              >
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Jahres-Vergleich</div>
                  <h4 className="text-lg md:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Plan-Werte über die Jahre
                  </h4>
                </div>
                <div className="space-y-3">
                  {budgetChartData.map((row) => {
                    const maxVal = Math.max(...budgetChartData.map(r => Math.max(r['Geplante Einnahmen'] as number, r['Geplante Ausgaben'] as number))) || 1;
                    const incomePct = ((row['Geplante Einnahmen'] as number) / maxVal) * 100;
                    const expensePct = ((row['Geplante Ausgaben'] as number) / maxVal) * 100;
                    const net = row['Netto'] as number;
                    return (
                      <div key={row.year} className="grid grid-cols-[60px_1fr] gap-3 items-center">
                        <div>
                          <div className="text-sm font-black text-gray-900 dark:text-white tabular-nums">{row.year}</div>
                          <div className={`text-[10px] font-bold tabular-nums ${net >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>
                            {formatCurrencyShort(net)}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div
                            className="h-5 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                          >
                            <div
                              className="absolute left-0 top-0 h-full rounded-full"
                              style={{
                                width: `${incomePct}%`,
                                background:
                                  'linear-gradient(90deg, rgba(16,185,129,0.50) 0%, rgba(16,185,129,0.20) 100%)',
                                borderRight: '1px solid rgba(16,185,129,0.50)',
                              }}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-900 dark:text-white tabular-nums">
                              +{formatCurrencyShort(row['Geplante Einnahmen'] as number)}
                            </span>
                          </div>
                          <div
                            className="h-5 rounded-full relative overflow-hidden bg-gray-100 dark:bg-white/[0.04]"
                          >
                            <div
                              className="absolute left-0 top-0 h-full rounded-full"
                              style={{
                                width: `${expensePct}%`,
                                background:
                                  'linear-gradient(90deg, rgba(244,63,94,0.50) 0%, rgba(244,63,94,0.20) 100%)',
                                borderRight: '1px solid rgba(244,63,94,0.50)',
                              }}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-900 dark:text-white tabular-nums">
                              −{formatCurrencyShort(row['Geplante Ausgaben'] as number)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            )}
          </section>
        )}

        {/* Multi-Step Modal — Story-Style Slide-up */}
        {showCreateModal && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' } as React.CSSProperties}
            onClick={resetModal}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-4xl flex flex-col overflow-hidden animate-slideUp bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 16px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}
              <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3" style={{ borderBottom: '1px solid #1e293b' }}>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">
                    Schritt {currentStep} von 3
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Neue Budget-Planung
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                    {currentStep === 1 && 'Grunddaten und Import-Optionen'}
                    {currentStep === 2 && 'Kategorien-Werte anpassen'}
                    {currentStep === 3 && 'Zusammenfassung und Bestätigung'}
                  </p>
                </div>
                <button
                  onClick={resetModal}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                </button>
              </div>

              {/* STEP-INDICATOR */}
              <div className="px-6 py-4 flex items-center gap-2 sm:gap-3" style={{ borderBottom: '1px solid #1e293b' }}>
                {[1, 2, 3].map((step, index) => (
                  <React.Fragment key={step}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className={`flex items-center justify-center w-7 h-7 rounded-full transition shrink-0 ${
                          step <= currentStep ? 'bg-primary-500 text-slate-950' : 'bg-white/5 text-slate-400'
                        }`}
                        style={{ border: step <= currentStep ? '1px solid rgba(16,185,129,0.50)' : '1px solid rgba(255,255,255,0.10)' }}
                      >
                        {step < currentStep ? <CheckIcon className="w-3.5 h-3.5" /> : <span className="text-xs font-black">{step}</span>}
                      </div>
                      <div className="hidden sm:block min-w-0">
                        <div className={`text-xs font-bold ${step <= currentStep ? 'text-white' : 'text-slate-500'}`}>
                          {step === 1 && 'Grunddaten'}
                          {step === 2 && 'Budget'}
                          {step === 3 && 'Bestätigen'}
                        </div>
                      </div>
                    </div>
                    {index < 2 && (
                      <div className={`flex-1 h-0.5 ${step < currentStep ? 'bg-primary-500' : 'bg-white/5'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* BODY */}
              <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
                {currentStep === 1 && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Name der Planung *</span>
                        <input
                          type="text"
                          value={stepData.step1.name}
                          onChange={(e) => setStepData(prev => ({ ...prev, step1: { ...prev.step1, name: e.target.value } }))}
                          placeholder="z. B. Budget 2026"
                          className="mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition placeholder-slate-500 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                          style={{ colorScheme: 'dark' }}
                        />
                      </label>
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Planungsjahr</span>
                        <input
                          type="number"
                          value={stepData.step1.year}
                          onChange={(e) => setStepData(prev => ({ ...prev, step1: { ...prev.step1, year: parseInt(e.target.value) } }))}
                          min="2020"
                          max="2030"
                          className="mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition tabular-nums bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                          style={{ colorScheme: 'dark' }}
                        />
                      </label>
                    </div>

                    <div
                      className="rounded-2xl p-4"
                      style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-3">
                        Ausgangsdaten
                      </div>
                      <div className="space-y-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="importMode"
                            checked={stepData.step1.useImport}
                            onChange={() => setStepData(prev => ({ ...prev, step1: { ...prev.step1, useImport: true } }))}
                            className="h-4 w-4 mt-1 accent-primary-500"
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-bold text-gray-900 dark:text-white">Aus vorhandenem Jahr importieren</div>
                            <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5">
                              Übernimmt vorhandene Kategorie-Beträge als Ausgangsbasis.
                            </div>
                            {stepData.step1.useImport && (
                              <div className="mt-2">
                                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Jahr für Import</span>
                                <input
                                  type="number"
                                  value={stepData.step1.importYear}
                                  onChange={(e) => setStepData(prev => ({ ...prev, step1: { ...prev.step1, importYear: parseInt(e.target.value) } }))}
                                  min="2020"
                                  max="2030"
                                  className="mt-1 w-32 px-3 py-2 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition tabular-nums bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                                  style={{ colorScheme: 'dark' }}
                                />
                              </div>
                            )}
                          </div>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="importMode"
                            checked={!stepData.step1.useImport}
                            onChange={() => setStepData(prev => ({ ...prev, step1: { ...prev.step1, useImport: false } }))}
                            className="h-4 w-4 mt-1 accent-primary-500"
                          />
                          <div>
                            <div className="text-sm font-bold text-gray-900 dark:text-white">Komplett neue Planung</div>
                            <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5">
                              Beginnt mit leeren Kategorien für komplette Neueingabe.
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Schritt 2</div>
                        <h4 className="text-lg font-black text-gray-900 dark:text-white tracking-tight">Kategorien bearbeiten</h4>
                      </div>
                      <button
                        onClick={addStep2Category}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Kategorie hinzufügen
                      </button>
                    </div>

                    {/* Summen-Tiles */}
                    <div className="grid grid-cols-3 gap-3">
                      {(() => {
                        const tin = Object.values(stepData.step2.budgetData).reduce((s, c: any) => s + (parseFloat(c.income) || 0), 0);
                        const tex = Object.values(stepData.step2.budgetData).reduce((s, c: any) => s + (parseFloat(c.expense) || 0), 0);
                        const tnet = tin - tex;
                        return (
                          <>
                            <div className="rounded-2xl p-3" style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Einnahmen</div>
                              <div className="text-lg font-black text-emerald-700 dark:text-emerald-300 tabular-nums mt-0.5">{formatCurrencyShort(tin)}</div>
                            </div>
                            <div className="rounded-2xl p-3" style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Ausgaben</div>
                              <div className="text-lg font-black text-rose-700 dark:text-rose-300 tabular-nums mt-0.5">{formatCurrencyShort(tex)}</div>
                            </div>
                            <div className="rounded-2xl p-3" style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Bilanz</div>
                              <div className={`text-lg font-black tabular-nums mt-0.5 ${tnet >= 0 ? 'text-primary-300' : 'text-amber-300'}`}>
                                {tnet >= 0 ? '+' : ''}{formatCurrencyShort(tnet)}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                      {Object.entries(stepData.step2.budgetData).map(([categoryName, data]) => {
                        const cat = categories.find(c => c.name === categoryName);
                        const Icon = cat?.icon ? (iconMap[cat.icon] || iconMap.default) : iconMap.default;
                        const net = parseFloat(data.income as string || '0') - parseFloat(data.expense as string || '0');
                        return (
                          <div
                            key={categoryName}
                            className="p-3 rounded-2xl space-y-2"
                            style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                          >
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                              <div className="flex items-center gap-2 min-w-0">
                                <div
                                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                                >
                                  <Icon className="w-4 h-4 text-primary-700 dark:text-primary-300" />
                                </div>
                                <span className="text-sm font-bold text-gray-900 dark:text-white truncate" title={categoryName}>{categoryName}</span>
                                <button
                                  onClick={() => removeStep2Category(categoryName)}
                                  className="ml-auto w-7 h-7 rounded-lg flex items-center justify-center text-rose-700 dark:text-rose-300 hover:bg-rose-500/10 transition"
                                  title="Kategorie entfernen"
                                >
                                  <XMarkIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <label className="block">
                                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Einnahmen €</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={data.income}
                                  onChange={(e) => handleStep2BudgetChange(categoryName, 'income', e.target.value)}
                                  placeholder="0.00"
                                  className="mt-1 w-full px-3 py-2 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition tabular-nums border border-gray-200 dark:border-slate-700"
                                  style={{ background: 'rgba(2,6,23,0.6)', colorScheme: 'dark' }}
                                />
                              </label>
                              <label className="block">
                                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Ausgaben €</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={data.expense}
                                  onChange={(e) => handleStep2BudgetChange(categoryName, 'expense', e.target.value)}
                                  placeholder="0.00"
                                  className="mt-1 w-full px-3 py-2 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition tabular-nums border border-gray-200 dark:border-slate-700"
                                  style={{ background: 'rgba(2,6,23,0.6)', colorScheme: 'dark' }}
                                />
                              </label>
                              <div>
                                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Netto</span>
                                <div
                                  className={`border border-gray-200 dark:border-slate-700 ${`mt-1 px-3 py-2 rounded-xl text-sm font-black tabular-nums ${net >= 0 ? 'text-primary-300' : 'text-rose-300'}`}`}
                                  style={{ background: 'rgba(2,6,23,0.6)' }}
                                >
                                  {formatCurrency(net)}
                                </div>
                              </div>
                            </div>
                            <input
                              type="text"
                              value={data.comment || ''}
                              onChange={(e) => handleStep2BudgetChange(categoryName, 'comment', e.target.value)}
                              placeholder="Kommentar (z. B. 120 Mitglieder × 69 €)..."
                              className="w-full px-3 py-2 rounded-xl text-xs italic text-gray-600 dark:text-slate-400 placeholder-slate-500 outline-none transition"
                              style={{
                                background: 'rgba(2,6,23,0.6)',
                                border: '1px solid #1e293b',
                                colorScheme: 'dark',
                              } as React.CSSProperties}
                            />
                          </div>
                        );
                      })}
                    </div>

                    {Object.keys(stepData.step2.budgetData).length === 0 && (
                      <div className="text-center py-8">
                        <TagIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-sm font-bold text-gray-900 dark:text-white">Noch keine Kategorien</p>
                        <p className="text-xs text-gray-600 dark:text-slate-400">Klicke auf "Kategorie hinzufügen" um zu beginnen</p>
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="rounded-2xl p-3" style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Name</div>
                        <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5">{stepData.step1.name || '—'}</div>
                      </div>
                      <div className="rounded-2xl p-3" style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Jahr</div>
                        <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">{stepData.step1.year}</div>
                      </div>
                      <div className="rounded-2xl p-3" style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}>
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Kategorien</div>
                        <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
                          {Object.keys(stepData.step2.budgetData).length}
                        </div>
                      </div>
                    </div>

                    {(() => {
                      const tin = Object.values(stepData.step2.budgetData).reduce((s, d) => s + (parseFloat(d.income as string || '0') || 0), 0);
                      const tex = Object.values(stepData.step2.budgetData).reduce((s, d) => s + (parseFloat(d.expense as string || '0') || 0), 0);
                      const tnet = tin - tex;
                      return (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="rounded-2xl p-4" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.30)' }}>
                            <div className="flex items-center gap-2">
                              <ArrowTrendingUpIcon className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
                              <span className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-bold">Gesamt Einnahmen</span>
                            </div>
                            <div className="text-2xl font-black text-emerald-700 dark:text-emerald-300 tabular-nums mt-1">{formatCurrency(tin)}</div>
                          </div>
                          <div className="rounded-2xl p-4" style={{ background: 'rgba(244,63,94,0.05)', border: '1px solid rgba(244,63,94,0.30)' }}>
                            <div className="flex items-center gap-2">
                              <ArrowTrendingDownIcon className="w-4 h-4 text-rose-700 dark:text-rose-300" />
                              <span className="text-[10px] uppercase tracking-widest text-rose-700 dark:text-rose-300 font-bold">Gesamt Ausgaben</span>
                            </div>
                            <div className="text-2xl font-black text-rose-700 dark:text-rose-300 tabular-nums mt-1">{formatCurrency(tex)}</div>
                          </div>
                          <div
                            className="rounded-2xl p-4"
                            style={
                              tnet >= 0
                                ? { background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.30)' }
                                : { background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.30)' }
                            }
                          >
                            <div className="flex items-center gap-2">
                              <ChartBarIcon className={`w-4 h-4 ${tnet >= 0 ? 'text-primary-300' : 'text-amber-300'}`} />
                              <span className={`text-[10px] uppercase tracking-widest font-bold ${tnet >= 0 ? 'text-primary-300' : 'text-amber-300'}`}>
                                Netto-Budget
                              </span>
                            </div>
                            <div className={`text-2xl font-black tabular-nums mt-1 ${tnet >= 0 ? 'text-primary-300' : 'text-amber-300'}`}>
                              {tnet >= 0 ? '+' : ''}{formatCurrency(tnet)}
                            </div>
                            <div className={`text-[11px] mt-0.5 ${tnet >= 0 ? 'text-primary-300/70' : 'text-amber-300/70'}`}>
                              {tnet >= 0 ? 'Erwarteter Überschuss' : 'Erwartetes Defizit'}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">Kategorien im Detail</div>
                      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                        {Object.entries(stepData.step2.budgetData).map(([categoryName, data]) => {
                          const income = parseFloat(data.income as string || '0') || 0;
                          const expense = parseFloat(data.expense as string || '0') || 0;
                          const net = income - expense;
                          return (
                            <div
                              key={categoryName}
                              className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl"
                              style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <TagIcon className="w-4 h-4 text-gray-600 dark:text-slate-400 shrink-0" />
                                <span className="text-sm font-bold text-gray-900 dark:text-white truncate">{categoryName}</span>
                              </div>
                              <div className="flex items-center gap-3 sm:gap-4 text-xs shrink-0">
                                <span className="text-emerald-700 dark:text-emerald-300 font-bold tabular-nums">+{formatCurrencyShort(income)}</span>
                                <span className="text-rose-700 dark:text-rose-300 font-bold tabular-nums">−{formatCurrencyShort(expense)}</span>
                                <span className={`font-black tabular-nums ${net >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>
                                  {formatCurrencyShort(net)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* FOOTER */}
              <div
                className="px-6 py-4 flex items-center justify-between gap-2"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold transition bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Zurück
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={resetModal}
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold transition bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10"
                  >
                    Abbrechen
                  </button>
                  {currentStep < 3 ? (
                    <button
                      onClick={nextStep}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950"
                    >
                      Weiter
                    </button>
                  ) : (
                    <button
                      onClick={handleCreateBudgetPlan}
                      disabled={loading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-950" />
                          Wird erstellt...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-4 h-4" />
                          Erstellen
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detail-Modal */}
        {showDetailModal && selectedBudgetPlan && (
          <BudgetDetailModal
            plan={selectedBudgetPlan}
            categories={categories}
            accountStatements={accountStatements}
            onClose={() => {
              setShowDetailModal(false);
              setIsFullscreen(false);
            }}
            onUpdate={(updatedPlan) => {
              setBudgetPlans(prev => prev.map(p => p.id === updatedPlan.id ? updatedPlan : p));
              setSelectedBudgetPlan(updatedPlan);
            }}
          />
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          visible={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          variant={confirmModal.variant}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          button={confirmModal.variant === 'single' ? {
            text: 'OK',
            onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
          } : undefined}
          buttons={confirmModal.variant === 'two' ? {
            cancel: {
              text: 'Abbrechen',
              onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
            },
            confirm: {
              text: 'Bestätigen',
              onPress: () => {
                if (confirmModal.onConfirm) {
                  confirmModal.onConfirm();
                }
                setConfirmModal({ ...confirmModal, isOpen: false });
              }
            }
          } : undefined}
        />

        {/* Category Input Modal — Slide-up */}
        {categoryInputModal.isOpen && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' } as React.CSSProperties}
            onClick={() => {
              setCategoryInputModal({ isOpen: false, title: '', onConfirm: () => {} });
              setCategoryInputValue('');
            }}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md flex flex-col overflow-hidden animate-slideUp bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid #1e293b' }}>
                <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Neue Kategorie</div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">{categoryInputModal.title}</h3>
              </div>
              <div className="px-6 py-5">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Kategorie-Name</span>
                  <input
                    type="text"
                    value={categoryInputValue}
                    onChange={(e) => setCategoryInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && categoryInputValue.trim()) {
                        categoryInputModal.onConfirm(categoryInputValue);
                      }
                    }}
                    autoFocus
                    placeholder="Name der neuen Kategorie"
                    className="mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition placeholder-slate-500 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                    style={{ colorScheme: 'dark' }}
                  />
                </label>
              </div>
              <div
                className="px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  onClick={() => {
                    setCategoryInputModal({ isOpen: false, title: '', onConfirm: () => {} });
                    setCategoryInputValue('');
                  }}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold transition bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => {
                    if (categoryInputValue.trim()) categoryInputModal.onConfirm(categoryInputValue);
                  }}
                  disabled={!categoryInputValue.trim()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hinzufügen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetPlanning;
